import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useBrandMode } from "./BrandModeContext";
import { fetchBrandHome, dealAction } from "./services/brandModeServices";
import { EMPTY_VALUE, fmtMoney } from "./brandUtils";
import { greeting, timeAgo } from "./brandDealUtils";
import {
  Avatar,
  Badge,
  Btn,
  Card,
  ErrorState,
  InsightBox,
  Loading,
  MetricCard,
  Overline,
  PageHeader,
  Select,
} from "./ui/BrandUI";

const ACTION_KINDS = [
  { value: "", label: "Action" },
  { value: "counter", label: "Counters" },
  { value: "submission", label: "Content reviews" },
  { value: "contract", label: "Contracts" },
  { value: "expiring", label: "Expiring offers" },
  { value: "draft", label: "Drafts" },
];

// A filter chip that is really a native <select>, so the dropdown works on
// every device while keeping the Figma chip look.
const ChipSelect = ({ value, onChange, options, placeholder }) => (
  <span className="relative inline-flex">
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="!h-10 !w-auto !rounded-full !pl-4 !pr-8 bm-label-l"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  </span>
);

const AttentionRow = ({ item, onFollowUp, busy }) => {
  const navigate = useNavigate();
  const onAction = () => {
    if (item.kind === "expiring") return onFollowUp(item);
    if (item.href) return navigate(item.href);
    return null;
  };
  return (
    <div className="flex items-center justify-between gap-4 border-b border-bm-mutedbg py-[14px] last:border-b-0">
      <div className="flex min-w-0 flex-col gap-[2px]">
        <div className="bm-h-xs text-bm-ink">{item.title}</div>
        <div className="flex flex-wrap items-center gap-3">
          {item.chip && (
            <Badge tone="soft" size="m">
              {item.chip}
            </Badge>
          )}
          <span className="bm-body-m text-bm-muted">
            {item.meta}
            {item.at ? ` · ${timeAgo(item.at)}` : ""}
          </span>
        </div>
      </div>
      <Btn variant="secondary" size="sm" onClick={onAction} disabled={busy}>
        {item.action}
      </Btn>
    </div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { refreshBadges } = useBrandMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const payload = await fetchBrandHome();
      setData(payload);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load your home view");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const attention = useMemo(() => {
    const list = data?.attention || [];
    return list.filter((item) => {
      if (kindFilter && item.kind !== kindFilter) return false;
      if (campaignFilter) {
        const name = (data?.campaignOptions || []).find((c) => c.id === campaignFilter)?.name;
        if (item.chip !== name && item.campaignId !== campaignFilter && !(item.meta || "").includes(name || " ")) {
          return false;
        }
      }
      return true;
    });
  }, [data, kindFilter, campaignFilter]);

  const onFollowUp = async (item) => {
    setBusy(true);
    try {
      await Promise.all((item.dealIds || []).map((id) => dealAction(id, "followup")));
      toast.success(`Follow-up sent to ${item.dealIds?.length || 0} athlete${item.dealIds?.length === 1 ? "" : "s"}.`);
      await load();
      refreshBadges();
    } catch (err) {
      toast.error(err.message || "Could not send follow-ups");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="py-10">
        <ErrorState>{error}</ErrorState>
      </div>
    );
  }
  if (!data) return <Loading />;

  const { profile, summary, portfolio, activity } = data;
  const fmtPct = (v) => (v === null || v === undefined ? EMPTY_VALUE : `${v}%`);

  return (
    <div>
      <PageHeader
        title={`${greeting()}, ${profile?.firstName || "there"}`}
        subtitle={
          summary.attentionCount
            ? `${summary.attentionCount} thing${summary.attentionCount === 1 ? "" : "s"} need${summary.attentionCount === 1 ? "s" : ""} you today across ${summary.activeCampaigns} active campaign${summary.activeCampaigns === 1 ? "" : "s"}.`
            : summary.activeCampaigns
              ? `Nothing needs you right now across ${summary.activeCampaigns} active campaign${summary.activeCampaigns === 1 ? "" : "s"}.`
              : "Start your first campaign to put offers in front of athletes."
        }
        actions={<Btn onClick={() => navigate("/brand/campaigns/new")}>New campaign</Btn>}
      />

      <div className="flex flex-col gap-4 pt-7">
        {/* KPI strip */}
        <div className="flex flex-wrap gap-3">
          <MetricCard
            muted
            label="Items that need your attention"
            value={summary.attentionCount}
            sub="actions waiting on you"
            className="!flex-none min-w-[240px]"
          />
          <MetricCard label="Active campaigns" value={summary.activeCampaigns} sub={summary.activeCampaignsSub} />
          <MetricCard label="Athletes under contract" value={summary.athletesUnderContract} sub="across all campaigns" />
          <MetricCard
            label="Committed spend"
            value={fmtMoney(summary.committedSpend)}
            sub={summary.budgeted ? `of ${fmtMoney(summary.budgeted)} budgeted` : "no budgets set yet"}
          />
          <MetricCard
            label="Paid to athletes"
            value={fmtMoney(summary.paidToAthletes)}
            sub={`${fmtMoney(summary.cacheFees)} in Cache fees`}
          />
        </div>

        {/* Needs your attention */}
        <Card padding="p-[22px]" className="flex flex-col gap-[14px]">
          <div className="flex items-center justify-between pb-[6px]">
            <Overline>Needs your attention</Overline>
            <span className="bm-body-s text-bm-tertiary">Sorted by urgency</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <ChipSelect
              value={campaignFilter}
              onChange={setCampaignFilter}
              placeholder="Campaign"
              options={(data.campaignOptions || []).map((c) => ({ value: c.id, label: c.name }))}
            />
            <ChipSelect value={kindFilter} onChange={setKindFilter} options={ACTION_KINDS} />
          </div>
          {attention.length ? (
            <div>
              {attention.map((item, i) => (
                <AttentionRow key={`${item.kind}-${item.href || i}`} item={item} onFollowUp={onFollowUp} busy={busy} />
              ))}
            </div>
          ) : (
            <p className="py-6 text-center bm-body-m text-bm-muted">
              {data.attention?.length ? "Nothing matches these filters." : "You're all caught up."}
            </p>
          )}
        </Card>

        {/* Portfolio + activity */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <InsightBox
            className="min-w-0 flex-1"
            title={`Portfolio analysis · ${portfolio.campaignCount} campaign${portfolio.campaignCount === 1 ? "" : "s"}`}
            tiles={[
              {
                label: "Acceptance rate",
                value: fmtPct(portfolio.acceptanceRate),
                sub: `${portfolio.offersSent} offer${portfolio.offersSent === 1 ? "" : "s"} sent`,
              },
              {
                label: "Avg. time to sign",
                value: portfolio.avgDaysToSign === null ? EMPTY_VALUE : `${portfolio.avgDaysToSign} days`,
                sub: "offer to signature",
              },
              {
                label: "Fulfillment",
                value: fmtPct(portfolio.fulfillment),
                sub: "approved on time",
              },
            ]}
          >
            {portfolio.narrative}
          </InsightBox>

          <Card padding="px-[22px] pb-[10px] pt-[22px]" className="flex w-full flex-col gap-[14px] lg:w-[420px]">
            <Overline>Recent activity</Overline>
            {activity?.length ? (
              activity.map((a) => (
                <button
                  type="button"
                  key={a.id}
                  onClick={() => a.href && navigate(a.href)}
                  className="flex items-center justify-between gap-3 border-b border-bm-mutedbg py-3 text-left last:border-b-0 hover:opacity-80"
                >
                  <span className="flex min-w-0 items-center gap-[10px]">
                    <Avatar initials={a.initials} size={24} tone={a.system ? "muted" : "outline"} />
                    <span className="truncate bm-body-m text-bm-ink">{a.text}</span>
                  </span>
                  <span className="shrink-0 bm-body-s text-bm-tertiary">{timeAgo(a.at, { short: true })}</span>
                </button>
              ))
            ) : (
              <p className="py-4 bm-body-m text-bm-muted">Activity from your deals will show up here.</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
